import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { EmptyState, Stack } from "@salesapp/ui";
import { crmApi, type FollowUpLead, type FollowUpTask } from "../../lib/crmApi";
import { useCrmHref } from "../../lib/customerUrl";
import { useLocale } from "../../shell/LocaleProvider";
import { ListWorkspace } from "./RecordWorkspace";
import { statusLabel } from "./SearchBar";

function leadName(lead: FollowUpLead): string {
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(" ");
  return name || lead.company_name || lead.lead_code || "Lead";
}

export function FollowUpsPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const { formatDate } = useLocale();
  const [leads, setLeads] = useState<FollowUpLead[]>([]);
  const [tasks, setTasks] = useState<FollowUpTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await crmApi.followUps(orgId);
      setLeads(data.leads);
      setTasks(data.tasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load today's follow-ups.");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const empty = !loading && !error && leads.length === 0 && tasks.length === 0;

  return (
    <ListWorkspace title="Follow-ups today">
      <p className="crm-help">Leads with a next action today, and open sales-cycle tasks due today.</p>
      {error ? <p className="crm-error">{error}</p> : null}
      {loading ? <p className="crm-help">Loading…</p> : null}
      {empty ? <EmptyState title="Nothing due today" description="Book a next action on a lead, or leave a sales-cycle task open, and it will show up here on that day." /> : null}
      {!loading && leads.length > 0 ? (
        <Stack gap={2}>
          <h2 className="crm-section-title">Leads</h2>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Company</th>
                <th>Next action</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>
                    <Link to={crmHref(`/leads/${lead.id}`)}>{leadName(lead)}</Link>
                  </td>
                  <td>{lead.company_name || "—"}</td>
                  <td>{lead.next_action_type ? statusLabel(lead.next_action_type) : "—"}</td>
                  <td>{lead.next_action_at ? formatDate(lead.next_action_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      ) : null}
      {!loading && tasks.length > 0 ? (
        <Stack gap={2}>
          <h2 className="crm-section-title">Sales-cycle tasks</h2>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Company</th>
                <th>Status</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <Link to={crmHref(`/sales-cycle/${task.run_id}`)}>{task.title || "Task"}</Link>
                  </td>
                  <td>
                    {task.company_id ? (
                      <Link to={crmHref(`/companies/${task.company_id}`)}>{task.company_name || "Company"}</Link>
                    ) : (
                      task.company_name || "—"
                    )}
                  </td>
                  <td>{task.status ? statusLabel(task.status) : "—"}</td>
                  <td>{task.due_at ? formatDate(task.due_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      ) : null}
    </ListWorkspace>
  );
}
